import useAdminForm from "~/hooks/useAdminForm"
import { RowItem } from "../Table"
import { useContext, useEffect } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import { LONG_TERM_RESERVATION_KINDS, longTermReservationKind } from "~/constants"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import { debounce } from "lodash"
import roundToMaxPrecision from "../../../../utils/roundToMaxPrecision"
import moment from "moment"
import InvoicesModalContent from "../InvoicesModalContent"
import IInvoice from "../../../../interfaces/IInvoice"
import copyText from "../../../../utils/copyText"
import api from "../../../../api2"
import parseDateTime from "../../../../utils/parseDateTime"

export interface IFormData {
  electric_rate: number
  rowItems: RowItem[]
}

const electricKinds: longTermReservationKind[] = [
  LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES,
  LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY,
]

type Props = {
  rowItem: RowItem
  isVisibleElectric: boolean
  setSelectedInvoice: (invoice: IInvoice) => void
  refreshLocalInvoices: () => void
}

export const useTableRow = ({ rowItem, isVisibleElectric, setSelectedInvoice, refreshLocalInvoices }: Props) => {
  const { resource, isChanged, setValue } = useAdminForm<IFormData>()

  const {
    camp,
    showPaymentModal,
    showMessageModal,
    showModal,
    updateLocalReservations,
    showNotification,
    setCurrentPage,
    setSelectedReservation,
  } = useContext(AdminContext)

  useEffect(() => {
    if (rowItem.invoice) {
      const electricItems = rowItem.invoice?.order?.line_items.filter((item) => item.kind === "electric")
      const total = electricItems.reduce((sum, item) => sum + item.total_amount_in_cents, 0)
      setValue(`rowItems.${thisIndex}.electricSubtotal`, total)
    }
  }, [rowItem.invoice?.order?.line_items])

  const selectable = !!rowItem.invoice
  const refId = rowItem.invoiceId || rowItem.siteId
  const thisIndex = resource.rowItems.findIndex((item) => item.invoiceId === refId || item.siteId === refId)
  const thisRowItem = rowItem.isSiteOnly
    ? rowItem
    : resource.rowItems.find((item) => item.invoiceId === refId || item.siteId === refId) || rowItem

  const isMissingContactInfo = !thisRowItem.invoice?.recipient_email && !thisRowItem.invoice?.recipient_phone
  const isShowingElectricFields =
    isVisibleElectric &&
    (electricKinds.includes(thisRowItem.reservationRecurringKind as longTermReservationKind) || thisRowItem.isSiteOnly)

  const hasSendDate =
    !thisRowItem.isSiteOnly &&
    thisRowItem?.invoice?.status !== INVOICE_STATUSES.DRAFT &&
    thisRowItem.invoiceSendDate &&
    !(thisRowItem?.invoice?.status === INVOICE_STATUSES.PAID && moment(thisRowItem.invoiceSendDate).isAfter(moment()))

  const isDueDateRed =
    thisRowItem?.invoice?.status === INVOICE_STATUSES.DRAFT &&
    parseDateTime(thisRowItem.invoice?.due_date, camp.timezone, { asMoment: true })?.isBefore(moment())

  const formattedDate = (date: string) => {
    const momentDate = parseDateTime(date, camp.timezone, { asMoment: true })
    if (!momentDate?.isValid()) return ""

    const isIncludingYear = momentDate.year() !== moment().year()
    return momentDate.format(isIncludingYear ? "MMM D 'YY" : "MMM D")
  }

  const onChangeLastMeterReading = debounce((e: { target: { value: any } }) => {
    const lastMeterReading = e.target.value
    const electricUsed = roundToMaxPrecision(Number(thisRowItem.currentMeterReading) - Number(lastMeterReading))
    setValue(`rowItems.${thisIndex}.lastMeterReading`, lastMeterReading)
    if (electricUsed >= 0) {
      setValue(`rowItems.${thisIndex}.electricUsed`, electricUsed)
    }
  }, 50)

  const onChangeMeterReading = debounce((e: { target: { value: any } }) => {
    const meterReading = e.target.value
    const electricUsed = roundToMaxPrecision(Number(meterReading) - Number(thisRowItem.lastMeterReading))
    setValue(`rowItems.${thisIndex}.currentMeterReading`, meterReading)
    if (electricUsed >= 0) {
      setValue(`rowItems.${thisIndex}.electricUsed`, electricUsed)
    }
  }, 50)

  const onChangeElectricUsed = debounce((e: { target: { value: any } }) => {
    const electricUsed = e.target.value
    if (Number(electricUsed) < 0) {
      return
    }
    const meterReading = roundToMaxPrecision(Number(thisRowItem.lastMeterReading) + Number(electricUsed))
    setValue(`rowItems.${thisIndex}.electricUsed`, electricUsed)
    setValue(`rowItems.${thisIndex}.currentMeterReading`, meterReading)
  }, 50)

  const dropdownOptionsFor = (invoice: IInvoice) => {
    const deletedAt = invoice.invoice?.deleted_at || invoice.reservation?.deleted_at

    const options = [
      {
        label: "Edit invoice",
        onClick: () => {
          setSelectedInvoice(invoice)
        },
      },
      {
        label: "Copy invoice link",
        onClick: () => {
          copyText(window.location.origin + `/i/${invoice.hash_id}`)
        },
      },
      {
        label: "View reservation",
        onClick: () => {
          setCurrentPage("reservations")
          setSelectedReservation(invoice.reservation)
        },
      },
      {
        label: "_divider_",
      },
      {
        label: "Send invoice",
        onClick: () => {
          showModal(
            <InvoicesModalContent
              invoices={[invoice]}
              onSuccess={(reservations) => {}}
              refreshLocalInvoices={refreshLocalInvoices}
            />,
          )
        },
      },
      {
        label: "Send message",
        onClick: () => {
          // TODO: message modal shouldn't expect only reservation
          showMessageModal({ reservations: [invoice.reservation] })
        },
      },
    ]

    if (deletedAt === null) {
      options.splice(4, 0, {
        label: "Take payment now",
        onClick: () => {
          // TODO: payment modal shouldn't expect reservation, but order
          showPaymentModal(invoice.order.unpaid_total_in_cents, { reservation: invoice.reservation })
        },
      })
    }

    if (invoice.status !== INVOICE_STATUSES.PAID) {
      options.splice(4, 0, {
        label: "Mark as paid",
        onClick: () => {
          api.Reservations.update(invoice.reservation?.id, {
            status: "paid",
            lock_version: invoice.reservation?.lock_version,
          })
            .then(({ reservation }) => {
              updateLocalReservations([reservation])
              refreshLocalInvoices()
              showNotification({
                type: "success",
                title: "Success",
                description: "Marked invoice as paid",
              })
            })
            .catch((err) => {
              showNotification({
                type: "error",
                title: "Error",
                description: err.message,
              })
            })
        },
      })
    }

    if (invoice.status !== INVOICE_STATUSES.PAID || deletedAt === null) {
      options.splice(-2, 0, {
        label: "_divider_",
      })
    }

    return options
  }

  return {
    isMissingContactInfo,
    thisRowItem,
    thisIndex,
    camp,
    selectable,
    isShowingElectricFields,
    isChanged,
    hasSendDate,
    isDueDateRed,
    formattedDate,
    onChangeLastMeterReading,
    onChangeMeterReading,
    onChangeElectricUsed,
    dropdownOptionsFor,
    setValue,
  }
}
